import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import SnackBarUpdateButtonTooltip from '@/features/InventoryPage/components/SnackBarUpdateButtonTooltip';
import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import TableInstance from '@/shared/constants/tableInstance';
import { AtomModel_VIEW } from '@/shared/types/atom.type';
import MetadataDefinitions from '@/shared/types/metadata/metadataDefinitions.type';

import useInventoryItemsTable_v2 from './hooks/useInventoryItemsTable_v2';

type InventoryItemsTable_v2Props = {
  refreshTableIndex: number;
  inventoryId: string;
  stashedMetadata: Record<string, unknown> | null;
  metadataDefinitions: MetadataDefinitions[];
  onUpdateSelectedItems: (items: AtomModel_VIEW[]) => void;
  setInventoryItemsTotal: Dispatch<SetStateAction<number>>;
};

const InventoryItemsTable_v2: FC<InventoryItemsTable_v2Props> = ({
  refreshTableIndex,
  inventoryId,
  stashedMetadata,
  metadataDefinitions,
  onUpdateSelectedItems,
  setInventoryItemsTotal,
}) => {
  const { t } = useTranslation(['inventory', 'common']);

  const { apiRef, model, table, columns, visibilityModel, setVisibilityModel } =
    useInventoryItemsTable_v2({
      metadataDefinitions,
      inventoryId,
      refreshTableIndex,
    });

  useEffect(
    () => setInventoryItemsTotal(table.totalRows),
    [setInventoryItemsTotal, table.totalRows],
  );

  return (
    <div style={{ height: '50vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.inventory_atom_id as string}
        columns={columns}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        customProps={{
          tableInstance: TableInstance.Atoms,
          apiRef,
          model,
          toolbar: {
            title: t('inventory:inventoryItems'),
            rowActions: [
              {
                type: 'default',
                button: {
                  label: t('common:edit'),
                  onClick: (apiRef, rowId) => {
                    const selectedRow = apiRef.current.getRow(
                      rowId || '',
                    ) as AtomModel_VIEW;

                    onUpdateSelectedItems([selectedRow]);
                  },
                },
              },
            ],
          },
          snackBarProps: {
            actions: [
              {
                type: 'default',
                button: {
                  label: t('common:update'),
                  onClick: (apiRef) => {
                    const selectedItems = [
                      ...apiRef.current.getSelectedRows().values(),
                    ] as AtomModel_VIEW[];

                    onUpdateSelectedItems(selectedItems);
                  },
                },
                badgeProps: stashedMetadata
                  ? {
                      badgeContent: Object.keys(stashedMetadata).length,
                      color: 'primary',
                    }
                  : undefined,
                tooltipProps: stashedMetadata
                  ? {
                      title: (
                        <SnackBarUpdateButtonTooltip
                          stashedMetadata={stashedMetadata}
                        />
                      ),
                      arrow: true,
                    }
                  : undefined,
              },
            ],
          },
        }}
      />
    </div>
  );
};

export default InventoryItemsTable_v2;
